import { Component, Input, OnChanges, SimpleChanges, ViewChild, AfterViewInit, QueryList, ViewChildren, OnInit, ChangeDetectorRef } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { Role } from '@data/models/role.model';
import { MatPaginator } from '@angular/material/paginator';
import { ProjectWithRoles } from '@data/models/project.model';
import { MatSort } from '@angular/material/sort';
import { checkboxStatusEnum, MenuCheckboxComponent } from '@shared/components/menu-checkbox/menu-checkbox.component';
import { pull, transform } from 'lodash';
import { FormGroup, FormControl } from '@angular/forms';
import { Authorization } from '@data/models/authorization.model';

@Component({

    selector: 'syn-projects-roles-table',
    templateUrl: 'projects-roles-table.component.html',
    styleUrls: ['projects-roles-table.component.scss']
})
export class ProjectsRolesTableComponent implements OnInit, OnChanges, AfterViewInit {
    @Input() projects: { [id: string]: ProjectWithRoles } = {};
    @Input() roles: Role[];
    @Input() initialSelection: Authorization[];
    @Input() multiple: boolean;

    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild(MatSort) sort: MatSort;
    @ViewChildren('rolesSubmenuItem') menuCheckboxesRoles: QueryList<MenuCheckboxComponent>;

    public displayedColumns: string[] = ['select', 'id', 'name', 'roles'];
    public dataSource = new MatTableDataSource<ProjectWithRoles>([]);
    public dataForm: FormGroup;
    public selection = new SelectionModel<ProjectWithRoles>(true, []);
    public showRoleField = true;

    constructor(private cdr: ChangeDetectorRef) { }

    public get actionButtonsDisabled(): boolean {
        return this.selection.selected.length === 0;
    }

    public get total(): number {
        return this.selection.selected.length;
    }

    public ngOnInit(): void {
        const projects = this.dataSource.connect().getValue();

        for (const project of projects) {
            const selectedProject = this.initialSelection.find(({ projectId }) => projectId === project.id);

            if (selectedProject) {
                // Select the previous projects.
                this.selection.select(project);

                // Restore previously selected roles.
                if (this.multiple) {
                    this.dataForm.controls[project.id].setValue(project.roles);
                } else if (project.roles.length !== 0) {
                    this.dataForm.controls[project.id].setValue(project.roles[0]);
                }
            }
        }
    }

    public ngOnChanges({ projects, multiple }: SimpleChanges) {
        if (projects) {
            this.dataSource = new MatTableDataSource<ProjectWithRoles>(Object.values(projects.currentValue));
            this.dataForm = new FormGroup({});
            Object.values(projects.currentValue).map((project: ProjectWithRoles) => this.dataForm.controls[project.id] = new FormControl(project.roles));
            this.updateTableData();
        }

        if (multiple) {
            // Multiple cannot be changed when component is created.
            this.showRoleField = false;
            this.cdr.detectChanges();
            this.showRoleField = true;
            this.cdr.detectChanges();
        }
    }

    public ngAfterViewInit(): void {
        this.updateTableData();
    }

    public masterToggle(): void | boolean {
        const selectionDataSource = this.isAllSelected() ? this.selection.clear() : this.dataSource.connect().value.forEach(row => this.selection.select(row));
        return selectionDataSource;
    }

    public isAllSelected() {
        return this.selection.selected.length === this.dataSource.data.length;
    }

    public applyFilter(event: Event) {
        const filterValue = (event.target as HTMLInputElement).value;
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    public updateSelectedRoles($event: MouseEvent, menuItem: MenuCheckboxComponent, role: Role): void {
        this.selection.selected.map((projectRow) => {
            if (menuItem.status === checkboxStatusEnum.CHECKED && !this.projects[projectRow.id].roles.includes(role.id)) {
                this.projects[projectRow.id].roles.push(role.id);
            } else if (menuItem.status === checkboxStatusEnum.UNCHECKED) {
                pull(this.projects[projectRow.id].roles, role.id);
            }

            this.dataForm.controls[projectRow.id].setValue(this.projects[projectRow.id].roles);
        });

        this.updateTableData();
        $event.stopPropagation();
    }

    public hasProjects(): boolean {
        return this.projects && Object.values(this.projects).length > 0;
    }

    public updateDataSource(projectId: string): void {
        const roles = this.dataForm.controls[projectId].value;

        if (this.multiple) {
            this.projects[projectId].roles = roles;
        } else {
            this.projects[projectId].roles = [roles];
        }
    }

    public async updateMenuRolesStatus(): Promise<void> {
        const roles: { [key: string]: number } = transform(this.roles, (acc, role) => acc[role.id] = 0, {});

        this.selection.selected.map(project => {
            project.roles.map(role => {
                roles[role]++;
            });
        });

        this.menuCheckboxesRoles.toArray().map(menuItem => {
            menuItem.switchStatus(this.getMenuItemStatus(roles[menuItem.label]));
        });
    }

    private getMenuItemStatus(nbItemSelect: number): checkboxStatusEnum {
        if (nbItemSelect > 0 && nbItemSelect === this.selection.selected.length) {
            return checkboxStatusEnum.CHECKED;
        } else if (nbItemSelect > 0) {
            return checkboxStatusEnum.INDETERMINATE;
        }
        return checkboxStatusEnum.UNCHECKED;
    }

    private updateTableData() {
        this.dataSource.paginator = this.paginator;
        this.dataSource.sort = this.sort;
    }
}
